import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { momentZodType } from 'lib/zod';
import { z } from 'zod';
import { shardCountOptions } from 'modules/projects/components/CreateProjectModal.constants';
import { UniqueIdentifierValues } from 'modules/projects/types';
import { DataPolicy } from '../types';
import {
  EMAIL,
  FIRST_NAME,
  LAST_NAME,
  ORGANIZATION_NAME,
  WEBSITE,
  SALESFORCE_ACCOUNT_ID,
  TRIAL_END_DATE,
  DATA_POLICY,
  PROJECT_NAME,
  PROJECT_TIME_ZONE,
  UNIQUE_IDENTIFIER,
  SHARD_COUNT,
  BOOTSTRAP_ACCOUNT,
} from '../constants';

const schema = z.object({
  /**
   * Organization Fields
   */
  [ORGANIZATION_NAME]: z.string().min(1, { message: 'Organization name is required' }),
  [WEBSITE]: z
    .string()
    .url({ message: 'Website URL is invalid' })
    .min(1, { message: 'Website is required' }),
  [SALESFORCE_ACCOUNT_ID]: z
    .string()
    .regex(new RegExp(/^$|[a-zA-Z0-9]{15,18}/g), { message: 'Salesforce account ID is invalid' })
    .max(18, { message: 'Salesforce account ID is invalid' }),
  [TRIAL_END_DATE]: momentZodType.nullable(),
  [DATA_POLICY]: z.string(),
  /**
   * Administrator Fields
   */
  [FIRST_NAME]: z.string().min(1, { message: 'First name is required' }),
  [LAST_NAME]: z.string().min(1, { message: 'Last name is required' }),
  [EMAIL]: z
    .string()
    .email({ message: 'Email is invalid' })
    .min(1, { message: 'Email is required' }),
  /**
   * Project Fields
   */
  [PROJECT_NAME]: z.string().min(1, { message: 'Project name is required' }),
  [PROJECT_TIME_ZONE]: z.string(),
  [UNIQUE_IDENTIFIER]: z.nativeEnum(UniqueIdentifierValues),
  [SHARD_COUNT]: z.number(),
  [BOOTSTRAP_ACCOUNT]: z.boolean(),
});

const defaultValues = {
  /**
   * Org Values
   */
  [ORGANIZATION_NAME]: '',
  [WEBSITE]: '',
  [SALESFORCE_ACCOUNT_ID]: '',
  [TRIAL_END_DATE]: null,
  [DATA_POLICY]: DataPolicy.UNRESTRICTED,
  /**
   * Administrator Values
   */
  [FIRST_NAME]: '',
  [LAST_NAME]: '',
  [EMAIL]: '',
  /**
   * Org Values
   */
  [PROJECT_NAME]: '',
  [PROJECT_TIME_ZONE]: 'America/Los_Angeles',
  [UNIQUE_IDENTIFIER]: UniqueIdentifierValues.emailOrUserId,
  [SHARD_COUNT]: shardCountOptions[0].value,
  [BOOTSTRAP_ACCOUNT]: false,
};

export type NewOrganizationFormSchemaType = Required<z.infer<typeof schema>>;

export const NewOrganizationFormProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const methods = useForm<NewOrganizationFormSchemaType>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
